'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Sparkles, CheckCircle, ArrowRight, Clock } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

type Tier = 'indie_label' | 'local_brand' | 'buyer';

interface TierConfig {
  id: Tier;
  label: string;
  tagline: string;
  perks: string[];
  accent: string;
  bg: string;
}

const TIERS: TierConfig[] = [
  {
    id: 'indie_label',
    label: 'Indie Labels',
    tagline: 'Get your pieces in front of AI-curated outfits',
    perks: ['Free listing during beta', 'Featured in outfit recommendations', 'Early partner badge', 'Brand profile page'],
    accent: '#C9A96E',
    bg: '#FFFBF5',
  },
  {
    id: 'local_brand',
    label: 'Local Brands',
    tagline: 'Reach style-conscious shoppers in your city',
    perks: ['Free listing during beta', 'Location-aware recommendations', 'Brand profile page', 'Early partner badge'],
    accent: '#8A7560',
    bg: '#F7F4EF',
  },
  {
    id: 'buyer',
    label: 'Buyers',
    tagline: 'Get early access to AI-styled outfits',
    perks: ['First access when we launch', 'Exclusive beta features', 'Shape the product with feedback', 'Trendsetter badge'],
    accent: '#1A1A1A',
    bg: '#F5F3F0',
  },
];

export default function WaitlistPage() {
  const [selectedTier, setSelectedTier] = useState<Tier>('buyer');
  const [formData, setFormData] = useState({ name: '', email: '', brandName: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const isBrand = selectedTier === 'indie_label' || selectedTier === 'local_brand';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    try {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
      const functionUrl = `${supabaseUrl}/functions/v1/send-feedback-email`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

      const body = {
        type: 'waitlist',
        tier: selectedTier,
        name: formData.name,
        email: formData.email,
        ...(isBrand && { brandName: formData.brandName }),
      };

      const res = await fetch(functionUrl, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to join waitlist');
      }

      setStatus('success');
      setFormData({ name: '', email: '', brandName: '' });
    } catch (err: unknown) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FAF7F2' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b" style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)' }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2">
              <AppLogo size={24} />
              <span className="font-serif-display text-lg sm:text-xl tracking-tight" style={{ color: '#1A1A1A' }}>
                Atelier.ai
              </span>
            </div>
            <Link
              href="/about"
              className="text-xs sm:text-sm font-medium transition-opacity hover:opacity-70"
              style={{ color: 'hsl(30 8% 52%)' }}
            >
              About
            </Link>
            <Link
              href="/admin"
              className="text-xs sm:text-sm font-medium transition-opacity hover:opacity-70"
              style={{ color: 'hsl(30 8% 52%)' }}
            >
              Admin
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full">
        {/* Hero */}
        <section className="max-w-2xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12 text-center animate-fade-in-up stagger-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-8" style={{ borderColor: '#C9A96E', backgroundColor: '#FFFBF5' }}>
            <Clock size={12} style={{ color: '#C9A96E' }} />
            <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#C9A96E' }}>
              Launching June 2026
            </span>
          </div>

          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6" style={{ color: '#1A1A1A' }}>
            Style, curated by AI.
            <br />
            <em>Coming this summer.</em>
          </h1>

          <p className="text-base sm:text-lg leading-relaxed mb-4" style={{ color: 'hsl(30 8% 38%)' }}>
            Atelier.ai is an AI-powered personal styling companion — matching you with outfits tailored to your occasion, aesthetic, and budget. We're opening early access in June 2026.
          </p>
          <p className="text-sm" style={{ color: 'hsl(30 8% 52%)' }}>
            Join the waitlist and we'll reach out with your invite before the public launch.
          </p>
        </section>

        {/* Tier selector + form */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16 animate-fade-in-up stagger-2">

          {/* Tier cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            {TIERS.map((tier) => (
              <button
                key={tier.id}
                onClick={() => setSelectedTier(tier.id)}
                className="text-left rounded-2xl p-5 border transition-all duration-200"
                style={{
                  backgroundColor: selectedTier === tier.id ? tier.bg : '#FFFFFF',
                  borderColor: selectedTier === tier.id ? tier.accent : 'hsl(35 20% 88%)',
                  boxShadow: selectedTier === tier.id ? `0 0 0 1.5px ${tier.accent}` : 'none',
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <span
                    className="text-xs font-semibold tracking-widest uppercase"
                    style={{ color: tier.accent }}
                  >
                    {tier.label}
                  </span>
                  {selectedTier === tier.id && (
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tier.accent }}>
                      <CheckCircle size={10} color="#fff" />
                    </div>
                  )}
                </div>
                <p className="text-xs leading-relaxed mb-4" style={{ color: 'hsl(30 8% 52%)' }}>
                  {tier.tagline}
                </p>
                <ul className="space-y-1.5">
                  {tier.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-1.5">
                      <Sparkles size={10} className="mt-0.5 flex-shrink-0" style={{ color: tier.accent }} />
                      <span className="text-xs" style={{ color: 'hsl(30 8% 38%)' }}>{perk}</span>
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>

          {/* Form */}
          {status === 'success' ? (
            <div
              className="rounded-2xl p-10 border flex flex-col items-center text-center gap-5"
              style={{ backgroundColor: '#FFFFFF', borderColor: 'hsl(35 20% 88%)' }}
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFFBF5' }}>
                <CheckCircle size={24} style={{ color: '#C9A96E' }} />
              </div>
              <div>
                <p className="font-serif-display text-2xl mb-2" style={{ color: '#1A1A1A' }}>You're on the list.</p>
                <p className="text-sm leading-relaxed" style={{ color: 'hsl(30 8% 52%)' }}>
                  We'll send your invite before the June 2026 launch. Keep an eye on your inbox.
                </p>
              </div>
              <button
                onClick={() => setStatus('idle')}
                className="text-xs font-medium underline transition-opacity hover:opacity-70"
                style={{ color: 'hsl(30 8% 52%)' }}
              >
                Add another email
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl p-6 sm:p-8 border space-y-5"
              style={{ backgroundColor: '#FFFFFF', borderColor: 'hsl(35 20% 88%)' }}
            >
              <div>
                <p className="text-xs font-medium tracking-widest uppercase mb-1" style={{ color: '#C9A96E' }}>
                  {TIERS.find((t) => t.id === selectedTier)?.label}
                </p>
                <h2 className="font-serif-display text-xl sm:text-2xl" style={{ color: '#1A1A1A' }}>
                  Request early access
                </h2>
              </div>

              <div className={`grid grid-cols-1 gap-5 ${isBrand ? 'sm:grid-cols-2' : ''}`}>
                <div>
                  <label htmlFor="name" className="block text-xs font-medium mb-1.5 tracking-wide" style={{ color: 'hsl(30 8% 38%)' }}>
                    {isBrand ? 'Contact Name' : 'Your Name'}
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={isBrand ? 'Jane Smith' : 'Your name'}
                    className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all focus:ring-2"
                    style={{
                      borderColor: 'hsl(35 20% 88%)',
                      backgroundColor: '#FAF7F2',
                      color: '#1A1A1A',
                    }}
                  />
                </div>

                {isBrand && (
                  <div>
                    <label htmlFor="brandName" className="block text-xs font-medium mb-1.5 tracking-wide" style={{ color: 'hsl(30 8% 38%)' }}>
                      Brand / Label Name
                    </label>
                    <input
                      id="brandName"
                      name="brandName"
                      type="text"
                      required
                      value={formData.brandName}
                      onChange={handleChange}
                      placeholder="Your brand name"
                      className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all focus:ring-2"
                      style={{
                        borderColor: 'hsl(35 20% 88%)',
                        backgroundColor: '#FAF7F2',
                        color: '#1A1A1A',
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-medium mb-1.5 tracking-wide" style={{ color: 'hsl(30 8% 38%)' }}>
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all focus:ring-2"
                  style={{
                    borderColor: 'hsl(35 20% 88%)',
                    backgroundColor: '#FAF7F2',
                    color: '#1A1A1A',
                  }}
                />
              </div>

              {status === 'error' && (
                <p className="text-xs px-4 py-3 rounded-xl border" style={{ color: '#B91C1C', backgroundColor: '#FEF2F2', borderColor: '#FECACA' }}>
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
                style={{ backgroundColor: '#1A1A1A', color: '#FFFFFF' }}
              >
                {status === 'sending' ? (
                  <span>Joining waitlist…</span>
                ) : (
                  <>
                    <span>Join the waitlist</span>
                    <ArrowRight size={15} />
                  </>
                )}
              </button>

              <p className="text-xs text-center" style={{ color: 'hsl(30 8% 52%)' }}>
                No spam. Just your invite when we're ready.
              </p>
            </form>
          )}
        </section>

        {/* Timeline strip */}
        <section className="border-t" style={{ borderColor: 'hsl(35 20% 88%)', backgroundColor: '#FFFFFF' }}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 animate-fade-in-up stagger-3">
            <p className="text-xs font-medium tracking-widest uppercase mb-8 text-center" style={{ color: '#C9A96E' }}>
              Launch Timeline
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {[
                { period: 'Now', label: 'Waitlist open', done: true },
                { period: 'May 2026', label: 'Beta invites sent', done: false },
                { period: 'June 2026', label: 'Public beta launch', done: false },
                { period: 'Late 2026', label: 'Full platform launch', done: false },
              ].map((step, i) => (
                <div key={i} className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: step.done ? '#C9A96E' : 'hsl(35 20% 88%)' }}
                    />
                    <span className="text-xs font-semibold" style={{ color: step.done ? '#C9A96E' : 'hsl(30 8% 52%)' }}>
                      {step.period}
                    </span>
                  </div>
                  <p className="text-sm pl-4" style={{ color: '#1A1A1A' }}>{step.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
